//#region src/deployment-config.ts
/**
* Canonical `lowercased-uuid` format (a definition with `format:
* 'lowercased-uuid'`). A lowercase 8-4-4-4-12 hex UUID — the shape of a
* lowercased `apps_catalog` app id (cf. `appFactoryCaConfigMapName`, which
* lowercases the id before it becomes a k8s resource name). Uppercase,
* whitespace, and the empty string are rejected. Exported so every layer
* (WS2 tests, WS5 CLI, and the WS4 bash regex, which must MIRROR this) checks
* the same grammar.
*/
const LOWERCASED_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/**
* Upper bound on a generic (format-less) string config VALUE — defense-in-depth
* against absurd input reaching the store / render path. `lowercased-uuid`
* values are already bounded by their regex, so this cap applies only to the
* format-less string branch.
*/
const MAX_STRING_VALUE_LENGTH = 1024;
/**
* The build-shipped SSOT of settable deployment-config keys. The two device-mTLS
* entries are the ONLY members in v1 (DC-2 / FR-12); new members arrive with a
* build. A key absent here can never be set (WS2 rejects it 400 — FR-1).
*
* Both entries share `coFlipGroup: 'device-mtls'` → SYS-066 flips them together
* (FR-6); both are `convergesLiveDps: true` → a config-apply re-drives live DPs
* (FR-5). `cutover: 'staged'` — device-mTLS is a coordinated flag-day, not an
* instant toggle. `valuesPath` strings verified against the real chart keys
* (§0): zion-cp `deviceMtls.enabled` (values.yaml:367-368); zion-cp-compositions
* `deviceMtls.enabled` (values.yaml:200) and `deviceMtls.factoryCaAppId`
* (values.yaml:191).
*/
const DEPLOYMENT_CONFIG_REGISTRY = Object.freeze([{
	key: "DEVICE_MTLS_ENABLED",
	valueType: "boolean",
	configClass: "topology",
	scopes: ["deployment"],
	default: false,
	description: "Enable device client-cert mTLS ingress (the :8446 gateway listener) fleet-wide.",
	delivery: {
		targets: [{
			chart: "zion-cp",
			valuesPath: "deviceMtls.enabled"
		}, {
			chart: "zion-cp-compositions",
			valuesPath: "deviceMtls.enabled"
		}],
		coFlipGroup: "device-mtls",
		cutover: "staged",
		convergesLiveDps: true
	}
}, {
	key: "DEVICE_MTLS_FACTORY_CA_APP_ID",
	valueType: "string",
	configClass: "topology",
	scopes: ["deployment"],
	default: "",
	format: "lowercased-uuid",
	description: "Lowercased apps_catalog UUID of the verify app whose factory CA is replicated (WS-A4).",
	delivery: {
		targets: [{
			chart: "zion-cp-compositions",
			valuesPath: "deviceMtls.factoryCaAppId"
		}],
		coFlipGroup: "device-mtls",
		cutover: "staged",
		convergesLiveDps: true
	}
}]);
/**
* Look up a definition by key. THROWS on an unregistered key — the registry is
* the security boundary (FR-1): a key that isn't here is not a real capability,
* so callers must treat "unknown key" as a hard reject (WS2 maps it to 400).
*/
function getDeploymentConfigDefinition(key) {
	const def = DEPLOYMENT_CONFIG_REGISTRY.find((d) => d.key === key);
	if (!def) throw new Error(`unknown deployment config key '${key}'`);
	return def;
}
/**
* Pure effective-value resolution (DC-5). Precedence:
*
*     stored ?? env ?? definition.default
*
* Uses `??` (nullish), NOT `||`, so a legitimately-falsy stored value — boolean
* `false`, numeric `0`, empty string — is HONORED and never skipped over to the
* env/default. THROWS on an unregistered key (via `getDeploymentConfigDefinition`).
*
* @param key      a registered definition key.
* @param sources  `stored` = the persisted store value (release-engine); `env` =
*                 the release-cp / mirror break-glass env input (WS4). Either may
*                 be `undefined` (or `null` for `stored`) when absent.
* @returns the effective value (untyped — the caller renders/uses it per the
*          definition's `valueType`).
*/
function resolveEffectiveDeploymentConfig(key, sources) {
	const def = getDeploymentConfigDefinition(key);
	return sources.stored ?? sources.env ?? def.default;
}
//#endregion
export { DEPLOYMENT_CONFIG_REGISTRY, LOWERCASED_UUID_PATTERN, MAX_STRING_VALUE_LENGTH, getDeploymentConfigDefinition, resolveEffectiveDeploymentConfig };

//# sourceMappingURL=deployment-config.js.map